import { faPlus, faTimes, faUpload, faSpinner, faCheckCircle, faExclamationTriangle, faCloudUpload, faFilePdf, faFileImage, faFileAlt, faDownload, faEye, faTrashAlt, faFile } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import Button from "components/base/Button";
import NoDataFound from "components/common/NoDataFound";
import FileListItem from "components/modules/project-management/todo-list/FileListItem";
import axiosInstance from "helpers/api";
import { useEffect, useState, useRef, useCallback } from "react";
import { Form, ProgressBar } from "react-bootstrap";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { dateFormatter } from "helpers/utils";

type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

type QueuedFile = {
  file: File;
  id: string;
  status: UploadStatus;
  progress: number;
  error?: string;
};

const DealDetailsAttachments = ({ attachmentsLv, onUploadSuccess }: any) => {
  const [attachmentList, setAttachmentList] = useState<any[]>([]);
  const [uploadQueue, setUploadQueue] = useState<QueuedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadingRef = useRef(false); // Track uploading state without re-renders
  let { id } = useParams();

  const generateFileId = () => Math.random().toString(36).substr(2, 9);

  const validateFile = (file: File): string | null => {
    // Updated to match backend's enhanced file types
    const allowedTypes = [
      'image/png', 'image/jpeg', 'image/gif', 'image/webp',
      'application/pdf', 
      'text/plain', 'text/csv',
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/zip',
      'application/x-zip-compressed'
    ];
    const maxSize = 25 * 1024 * 1024; // 25MB to match backend
    
    if (!allowedTypes.includes(file.type)) {
      return 'File type not allowed. Supported: Images (PNG, JPG, GIF), Documents (PDF, Word, Excel), Text files, and ZIP archives.';
    }
    
    if (file.size > maxSize) {
      return `File size exceeds ${maxSize / (1024 * 1024)}MB limit.`;
    }
    
    return null;
  };

  const addFilesToQueue = (files: FileList | File[]) => {
    const filesArray = Array.from(files);
    const newQueuedFiles: QueuedFile[] = [];
    
    filesArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        toast.error(`${file.name}: ${error}`);
        return;
      }
      
      newQueuedFiles.push({
        file,
        id: generateFileId(),
        status: 'pending',
        progress: 0,
      });
    });
    
    if (newQueuedFiles.length > 0) {
      setUploadQueue(prev => [...prev, ...newQueuedFiles]);
    }
  };

  const uploadWithRetry = async (formData: FormData, fileName: string, fileId: string, maxRetries = 3) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await axiosInstance.post(`/deals/${id}/attachments`, formData);
        return response;
      } catch (error: any) {
        if (error.response?.status === 429 && attempt < maxRetries) {
          // Rate limit hit - use exponential backoff
          const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
          console.log(`Rate limit hit for ${fileName}, retrying in ${delay}ms (attempt ${attempt}/${maxRetries})`);
          updateFileStatus(fileId, 'pending', 0, `Rate limited - retrying in ${Math.round(delay/1000)}s`);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else if (error.response?.data?.duplicate_prevented) {
          // Handle duplicate prevention as success
          console.log(`Duplicate prevented for ${fileName}, using existing attachment`);
          return error.response;
        } else {
          throw error;
        }
      }
    }
  };

  const updateFileStatus = (fileId: string, status: UploadStatus, progress: number = 0, error?: string) => {
    setUploadQueue(prev => prev.map(file => 
      file.id === fileId ? { ...file, status, progress, error } : file
    ));
  };

  const removeFromQueue = (fileId: string) => {
    setUploadQueue(prev => prev.filter(file => file.id !== fileId));
  };

  const processUploadQueue = useCallback(async () => {
    if (uploadingRef.current) return;
    
    uploadingRef.current = true;
    setIsUploading(true);
    
    try {
      // Process files sequentially
      let hasMoreFiles = true;
      
      while (hasMoreFiles) {
        // Get current queue state
        const currentQueue = await new Promise<QueuedFile[]>(resolve => {
          setUploadQueue(current => {
            resolve([...current]);
            return current;
          });
        });
        
        const pendingFile = currentQueue.find(file => file.status === 'pending');
        
        if (!pendingFile) {
          hasMoreFiles = false;
          break;
        }
        
        try {
          updateFileStatus(pendingFile.id, 'uploading', 50);
          
          const formData = new FormData();
          formData.append("dealAttachment", pendingFile.file);
          
          const response = await uploadWithRetry(formData, pendingFile.file.name, pendingFile.id);
          
          if (response && response.data) {
            // Upload succeeded - show success briefly then remove from queue
            updateFileStatus(pendingFile.id, 'success', 100);
            
            const attachment = response.data.data?.attachment;
            const isDuplicate = response.data.data?.duplicate_prevented;
            
            if (attachment) {
              // Add to attachment list if not already there
              setAttachmentList(prev => {
                const exists = prev.some((a: any) => a.id === attachment.id);
                if (!exists) {
                  return [...prev, attachment];
                }
                return prev;
              });
              
              // Show appropriate message
              if (isDuplicate) {
                toast(`${pendingFile.file.name} already uploaded`, {
                  icon: '✓',
                  duration: 2000
                });
              } else {
                toast.success(`${pendingFile.file.name} uploaded successfully`);
              }
            }
            
            // REMOVE FROM QUEUE IMMEDIATELY on success
            removeFromQueue(pendingFile.id);
            
            // Trigger parent refresh callback if provided
            if (onUploadSuccess && !isDuplicate) {
              // Delay slightly to allow backend processing to start
              setTimeout(() => {
                onUploadSuccess();
              }, 1000);
            }
            
          } else {
            throw new Error('Upload failed - no response received');
          }
          
          // Delay between uploads to respect rate limits
          await new Promise(resolve => setTimeout(resolve, 1000));
          
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
          updateFileStatus(pendingFile.id, 'error', 0, errorMessage);
          console.error(`Failed to upload ${pendingFile.file.name}:`, error);
          
          // Show error for 3 seconds then remove from queue
          setTimeout(() => {
            console.log(`Removing failed ${pendingFile.file.name} from queue`);
            removeFromQueue(pendingFile.id);
          }, 3000);
          
          // Small delay before next file
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    } finally {
      uploadingRef.current = false;
      setIsUploading(false);
    }
  }, [id]);

  useEffect(() => {
    setAttachmentList(attachmentsLv || []);
  }, [attachmentsLv]);
  
  // Periodically check for AI processing updates
  useEffect(() => {
    const checkProcessingStatus = async () => {
      // Check if any attachments are in pending/processing state
      const hasProcessingItems = attachmentList.some(
        (att: any) => att.processing_status === 'pending' || att.processing_status === 'processing'
      );
      
      if (!hasProcessingItems || !id) return;
      
      try {
        // Fetch updated attachment list
        const response = await axiosInstance.get(`/deals/${id}`);
        if (response.data?.data?.deal?.DealAttachments) {
          const updatedAttachments = response.data.data.deal.DealAttachments;
          
          // Update processing status for attachments
          setAttachmentList(prev => 
            prev.map((att: any) => {
              const updated = updatedAttachments.find((u: any) => u.id === att.id);
              if (updated && updated.processing_status !== att.processing_status) {
                // Show toast for completed analysis
                if (updated.processing_status === 'completed' && att.processing_status !== 'completed') {
                  toast.success(`AI analysis completed for ${att.attachment_title}`);
                  
                  // If it's a contract and workflow was generated, show additional info
                  if (updated.ai_analysis?.workflow?.tasks?.length > 0) {
                    toast.success(`Generated ${updated.ai_analysis.workflow.tasks.length} workflow tasks`, {
                      duration: 5000
                    });
                    
                    // Trigger refresh when contract analysis completes
                    if (onUploadSuccess) {
                      onUploadSuccess();
                    }
                  }
                }
                return { ...att, ...updated };
              }
              return att;
            })
          );
        }
      } catch (error) {
        console.error('Failed to check processing status:', error);
      }
    };
    
    // Check every 5 seconds
    const interval = setInterval(checkProcessingStatus, 5000);
    
    // Initial check after 2 seconds
    const timeout = setTimeout(checkProcessingStatus, 2000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [attachmentList, id]);

  useEffect(() => {
    // Start processing when new files are added
    const hasPendingFiles = uploadQueue.some(file => file.status === 'pending');
    if (hasPendingFiles && !uploadingRef.current) {
      processUploadQueue();
    }
  }, [uploadQueue, processUploadQueue]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      addFilesToQueue(files);
    }
    // Reset input value to allow re-selecting same files
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      addFilesToQueue(files);
    }
  };

  const removeQueuedFile = (fileId: string) => {
    setUploadQueue(prev => prev.filter(file => file.id !== fileId));
  };

  const clearCompletedUploads = () => {
    setUploadQueue(prev => prev.filter(file => file.status === 'pending' || file.status === 'uploading'));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: UploadStatus) => {
    switch (status) {
      case 'pending':
        return <FontAwesomeIcon icon={faUpload} className="text-muted" />;
      case 'uploading':
        return <FontAwesomeIcon icon={faSpinner} className="text-primary" spin />;
      case 'success':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
      case 'error':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" />;
      default:
        return null;
    }
  };

  const getFileTypeIcon = (fileName: string | undefined) => {
    if (!fileName) return <FontAwesomeIcon icon={faFile} className="text-muted fs-2" />;
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FontAwesomeIcon icon={faFilePdf} className="text-danger fs-2" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return <FontAwesomeIcon icon={faFileImage} className="text-primary fs-2" />;
      case 'txt':
      case 'doc':
      case 'docx':
        return <FontAwesomeIcon icon={faFileAlt} className="text-info fs-2" />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <FontAwesomeIcon icon={faFileAlt} className="text-success fs-2" />;
      case 'zip':
        return <FontAwesomeIcon icon={faFile} className="text-warning fs-2" />;
      default:
        return <FontAwesomeIcon icon={faFile} className="text-muted fs-2" />;
    }
  };

  const getFileTypeColor = (fileName: string | undefined) => {
    if (!fileName) return 'light';
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'danger';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'primary';
      case 'txt':
      case 'doc':
      case 'docx':
        return 'info';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'success';
      default:
        return 'light';
    }
  };

  const formatUploadDate = (dateString: string) => {
    return dateFormatter(dateString);
  };

  const handleDeleteAttachment = async (attachmentId: any) => {
    try {
      const response = await axiosInstance.delete(
        `/deals/${id}/attachments/${attachmentId}`
      );
      toast.success(response.data.message);
      setAttachmentList(
        attachmentList.filter(
          (attachment: any) => attachment.id !== attachmentId
        )
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete attachment');
    }
  };

  // Auto-process contract attachments indicator
  const hasContractInQueue = uploadQueue.some(file => {
    const name = file.file.name.toLowerCase();
    return name.includes('contract') || name.includes('agreement') || name.includes('purchase');
  });

  return (
    <>
      <style>{`
        .attachment-item {
          transition: all 0.2s ease;
        }
        .attachment-item:hover {
          background-color: rgba(0,0,0,0.02);
        }
        .upload-queue-item {
          transition: all 0.2s ease;
        }
        .upload-queue-item:hover {
          transform: translateX(2px);
        }
        .drag-drop-zone {
          transition: all 0.3s ease;
        }
        .drag-drop-zone:hover {
          background-color: rgba(13, 110, 253, 0.05);
          border-color: #0d6efd;
        }
        .processing-indicator {
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
      `}</style>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-4">Attachments</h2>
        <div className="d-flex gap-2">
          {uploadQueue.length > 0 && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={clearCompletedUploads}
            >
              Clear Completed
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<FontAwesomeIcon icon={faUpload} />}
          >
            Browse Files
          </Button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.zip"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Drag and Drop Zone */}
      <div
        className={classNames(
          "border-2 border-dashed rounded-3 p-4 mb-4 text-center transition-all drag-drop-zone",
          {
            "border-primary bg-primary-subtle": isDragOver,
            "border-secondary": !isDragOver,
          }
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ cursor: 'pointer' }}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="py-4">
          <FontAwesomeIcon 
            icon={faCloudUpload} 
            className={classNames("fs-1 mb-3", {
              "text-primary": isDragOver,
              "text-muted": !isDragOver,
            })}
          />
          <h5 className="mb-2">
            {isDragOver ? "Drop files here" : "Drag & drop files here"}
          </h5>
          <p className="text-muted mb-2">
            or <span className="text-primary">browse files</span>
          </p>
          <small className="text-muted">
            Images, PDFs, Word/Excel documents, and ZIP files up to 25MB each
          </small>
        </div>
      </div>

      {/* Contract Processing Notice */}
      {hasContractInQueue && (
        <div className="alert alert-info d-flex align-items-center mb-4">
          <FontAwesomeIcon icon={faSpinner} className="text-info me-2 processing-indicator" spin />
          <div>
            <strong>Contract detected!</strong> We'll automatically analyze it and generate workflow tasks after upload.
          </div>
        </div>
      )}

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <div className="mb-4">
          <h6 className="mb-3">Upload Queue ({uploadQueue.length})</h6>
          <div className="upload-queue">
            {uploadQueue.map((queuedFile) => (
              <div key={queuedFile.id} className="card border-0 shadow-sm mb-3 upload-queue-item">
                <div className="card-body p-3">
                  <div className="d-flex align-items-start">
                    <div className="me-3 flex-shrink-0">
                      <div className={`p-2 rounded-2 bg-${getFileTypeColor(queuedFile.file.name)}-subtle`}>
                        {getFileTypeIcon(queuedFile.file.name)}
                      </div>
                    </div>
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <h6 className="mb-0 text-truncate" title={queuedFile.file.name}>
                          {queuedFile.file.name}
                        </h6>
                        <div className="d-flex align-items-center gap-2 ms-2">
                          <div className="d-flex align-items-center">
                            {getStatusIcon(queuedFile.status)}
                            <span className={classNames("badge ms-1", {
                              "bg-warning": queuedFile.status === 'pending',
                              "bg-primary": queuedFile.status === 'uploading',
                              "bg-success": queuedFile.status === 'success',
                              "bg-danger": queuedFile.status === 'error'
                            })}>
                              {queuedFile.status === 'uploading' ? 'Uploading...' : queuedFile.status}
                            </span>
                          </div>
                          {queuedFile.status === 'pending' && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeQueuedFile(queuedFile.id)}
                              className="p-1"
                            >
                              <FontAwesomeIcon icon={faTimes} className="fs-8" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="small text-muted mb-2">
                        {formatFileSize(queuedFile.file.size)}
                      </div>
                      {queuedFile.status === 'uploading' && (
                        <ProgressBar 
                          now={queuedFile.progress} 
                          className="mb-1"
                          style={{ height: '4px' }}
                          variant="primary"
                          animated
                        />
                      )}
                      {queuedFile.status === 'error' && queuedFile.error && (
                        <div className="alert alert-danger py-2 px-3 mb-0 small">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                          {queuedFile.error}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing Attachments */}
      <div>
        {attachmentList.length > 0 ? (
          <div className="border rounded-3 overflow-hidden">
            {attachmentList.map((attachment: any, index: any) => (
              <div 
                key={attachment.id || index} 
                className={classNames(
                  "d-flex align-items-center p-3 attachment-item",
                  { "border-bottom": index < attachmentList.length - 1 }
                )}
              >
                <div className="me-3">
                  {getFileTypeIcon(attachment.attachment_title)}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className="fw-semibold text-truncate mb-1">
                    <a
                      href={attachment.attachment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                    >
                      {attachment.attachment_title?.replace(/[^\x20-\x7E]/g, '') || 'Unknown file'}
                    </a>
                  </div>
                  <div className="small text-muted">
                    {attachment.attachment_size ? formatFileSize(attachment.attachment_size) : ''}
                    {attachment.attachment_size && attachment.created_on && (
                      <span className="mx-2">•</span>
                    )}
                    {attachment.created_on && (
                      <span>{formatUploadDate(attachment.created_on)}</span>
                    )}
                    {/* AI Processing Status */}
                    {attachment.processing_status === 'pending' && (
                      attachment.attachment_title?.toLowerCase().match(/contract|agreement|purchase/) ? (
                        <>
                          <span className="mx-2">•</span>
                          <span className="text-warning">
                            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                            AI analysis pending...
                          </span>
                        </>
                      ) : null
                    )}
                    {attachment.processing_status === 'processing' && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="text-primary">
                          <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                          AI analyzing document...
                        </span>
                      </>
                    )}
                    {attachment.processing_status === 'completed' && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="text-success">
                          <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                          AI analysis complete
                        </span>
                      </>
                    )}
                    {attachment.processing_status === 'failed' && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="text-danger">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                          AI analysis failed
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="d-flex gap-2 ms-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => window.open(attachment.attachment_link, '_blank')}
                    title="View file"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = attachment.attachment_link;
                      link.download = attachment.attachment_title || 'download';
                      link.click();
                    }}
                    title="Download file"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteAttachment(attachment.id)}
                    title="Delete file"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <div className="mb-3">
              <FontAwesomeIcon icon={faFile} className="text-muted" style={{ fontSize: '3rem' }} />
            </div>
            <h5 className="text-muted mb-2">No attachments yet</h5>
            <p className="text-muted mb-0">
              Drag & drop files above or use the browse button to get started
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default DealDetailsAttachments;